import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CarCard from "@/components/CarCard";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const STATO_MAP = { disponibili: "available", vendute: "sold" };
const PAGE_SIZE = 12;

export default function CarsPage() {
  const { lang } = useLang();
  const [searchParams] = useSearchParams();
  const initialStatus = STATO_MAP[searchParams.get("stato")] || "all";

  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [status, setStatus] = useState(initialStatus);
  const [page, setPage] = useState(1);

  // Debounce ricerca per non chiamare il backend a ogni tasto
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Sync URL param stato → state
  useEffect(() => {
    const s = STATO_MAP[searchParams.get("stato")] || "all";
    setStatus(s);
    setPage(1);
  }, [searchParams]);

  // Reset a pagina 1 quando cambiano filtri
  useEffect(() => { setPage(1); }, [debouncedQ, sort, status]);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: PAGE_SIZE, status, sort };
    if (debouncedQ) params.q = debouncedQ;
    api.get("/cars", { params })
      .then((r) => {
        setCars(r.data || []);
        const t = Number(r.headers?.["x-total-count"] || 0);
        setTotal(t);
      })
      .catch(() => { setCars([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, status, sort, debouncedQ]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const goTo = (p) => {
    const next = Math.min(totalPages, Math.max(1, p));
    if (next !== page) {
      setPage(next);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Costruisce l'elenco dei numeri di pagina da mostrare (max 7 slot con ellissi)
  const buildPageList = () => {
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    const arr = [...pages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      if (i > 0 && arr[i] - arr[i - 1] > 1) out.push("…");
      out.push(arr[i]);
    }
    return out;
  };

  return (
    <div className="pt-40 pb-32 px-6 sm:px-10 lg:px-14 max-w-[1600px] mx-auto" data-testid="cars-page">
      <div className="flex items-center gap-4 mb-8">
        <span className="w-10 h-px bg-white/40" />
        <span className="label-eyebrow text-white/70">{lang === "en" ? "Catalogue" : "Catalogo"}</span>
      </div>
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-white"
      >
        {lang === "en" ? "All cars" : "Tutte le vetture"}<br />
        <span className="italic text-neutral-500">{lang === "en" ? "for sale." : "in vendita."}</span>
      </motion.h1>

      <div className="mt-16 flex flex-wrap gap-4 items-center border-y border-white/10 py-6 sticky top-16 bg-black/80 backdrop-blur-lg z-30">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={lang === "en" ? "Search make, model, fuel…" : "Cerca marca, modello, alimentazione…"}
          className="flex-1 min-w-[240px] bg-transparent border border-white/10 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40 transition-colors"
          data-testid="cars-search-input"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-black border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/40"
          data-testid="cars-sort-select"
        >
          <option value="recent">{lang === "en" ? "Most recent" : "Più recenti"}</option>
          <option value="price_desc">{lang === "en" ? "Price: high → low" : "Prezzo: alto → basso"}</option>
          <option value="price_asc">{lang === "en" ? "Price: low → high" : "Prezzo: basso → alto"}</option>
          <option value="year_desc">{lang === "en" ? "Year: new → old" : "Anno: nuovo → vecchio"}</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-black border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/40"
          data-testid="cars-status-select"
        >
          <option value="all">{lang === "en" ? "All" : "Tutte"}</option>
          <option value="available">{lang === "en" ? "Available" : "Disponibili"}</option>
          <option value="sold">{lang === "en" ? "Sold" : "Vendute"}</option>
        </select>
      </div>

      {loading ? (
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="aspect-[4/3] bg-neutral-900/60 animate-pulse" />
          ))}
        </div>
      ) : cars.length === 0 ? (
        <div className="mt-24 border border-white/10 py-24 text-center">
          <p className="label-eyebrow text-neutral-500 mb-4">{lang === "en" ? "No results" : "Nessun risultato"}</p>
          <p className="font-serif italic text-2xl text-neutral-400">{lang === "en" ? "Try adjusting the filters." : "Prova a modificare i filtri."}</p>
        </div>
      ) : (
        <>
          <div className="mt-6 mb-8 label-eyebrow text-neutral-500" data-testid="cars-total-count">
            {total} {lang === "en" ? "cars" : "vetture"} · {lang === "en" ? "page" : "pagina"} {page}/{totalPages}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20">
            {cars.map((c, i) => (
              <CarCard key={c.id} car={c} index={i} />
            ))}
          </div>

          {totalPages > 0 && (
            <nav
              className="mt-20 flex items-center justify-center gap-2 flex-wrap"
              aria-label={lang === "en" ? "Pagination" : "Paginazione"}
              data-testid="cars-pagination"
            >
              <button
                onClick={() => goTo(page - 1)}
                disabled={page <= 1}
                className="inline-flex items-center gap-2 border border-white/10 px-5 py-2.5 text-white hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-prev"
              >
                <ChevronLeft size={14} /> {lang === "en" ? "Previous" : "Precedente"}
              </button>
              {totalPages > 1 && buildPageList().map((p, idx) =>
                p === "…" ? (
                  <span key={`e-${idx}`} className="px-3 text-neutral-500">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => goTo(p)}
                    className={`min-w-[44px] px-3 py-2 border transition-colors ${
                      p === page
                        ? "bg-white text-black border-white"
                        : "text-white border-white/10 hover:border-white/40"
                    }`}
                    data-testid={`pagination-page-${p}`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => goTo(page + 1)}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-2 border border-white/10 px-5 py-2.5 text-white hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-next"
              >
                {lang === "en" ? "Next" : "Successivo"} <ChevronRight size={14} />
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
